/**
 * Response Builder Projects Repository
 * 
 * Handles rb_projects collection operations.
 * Phase D1 - Data Model Activation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  type Firestore,
} from 'firebase/firestore';

import type {
  RbProject,
  CreateProjectInput,
  ProjectStatus,
} from './types/rb';

/**
 * Validate required fields for project creation
 */
function validateCreateInput(input: CreateProjectInput): void {
  const required = [
    'workspaceId',
    'title',
    'jurisdiction',
    'intent',
    'sourceDocumentIds',
    'createdByUserId',
  ];

  for (const field of required) {
    if (!input[field as keyof CreateProjectInput]) {
      throw new Error(`[rbProjectsRepository] Missing required field: ${field}`);
    }
  }

  // Validate sourceDocumentIds is non-empty array
  if (!Array.isArray(input.sourceDocumentIds) || input.sourceDocumentIds.length === 0) {
    throw new Error('[rbProjectsRepository] sourceDocumentIds must be a non-empty array');
  }

  // Validate intent is one of the allowed values
  const validIntents = ['RFP', 'RFI', 'GRANT', 'OTHER'];
  if (!validIntents.includes(input.intent)) {
    throw new Error(`[rbProjectsRepository] Invalid intent: ${input.intent}. Must be one of: ${validIntents.join(', ')}`);
  }
}

/**
 * Create a new project record
 * 
 * Phase D1: Project creation fails unless sourceDocumentIds.length >= 1
 * Every sourceDocumentId must exist in rb_documents in the same workspace
 * 
 * @throws if any required field is missing or validation fails
 */
export async function createProject(
  db: Firestore,
  projectId: string,
  input: CreateProjectInput
): Promise<RbProject> {
  validateCreateInput(input);

  const now = Timestamp.now();
  const projectRef = doc(db, 'rb_projects', projectId);

  const project: Omit<RbProject, 'id'> = {
    workspaceId: input.workspaceId,
    title: input.title,
    jurisdiction: input.jurisdiction,
    intent: input.intent,
    sourceDocumentIds: input.sourceDocumentIds,
    status: 'created',
    createdAt: now,
    createdByUserId: input.createdByUserId,
    updatedAt: now,
  };

  await setDoc(projectRef, project);

  return {
    id: projectId,
    ...project,
  };
}

/**
 * Get project by ID
 */
export async function getProject(
  db: Firestore,
  projectId: string
): Promise<RbProject | null> {
  const projectRef = doc(db, 'rb_projects', projectId);
  const projectSnap = await getDoc(projectRef);

  if (!projectSnap.exists()) {
    return null;
  }

  return {
    id: projectSnap.id,
    ...projectSnap.data(),
  } as RbProject;
}

/**
 * List projects for a workspace
 * Ordered by createdAt descending (newest first)
 */
export async function listProjects(
  db: Firestore,
  workspaceId: string
): Promise<RbProject[]> {
  const projectsRef = collection(db, 'rb_projects');
  const q = query(
    projectsRef,
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as RbProject[];
}

/**
 * Update project status
 * 
 * Phase D6: Used to set status to 'failed' on extraction errors
 */
export async function updateProjectStatus(
  db: Firestore,
  projectId: string,
  status: ProjectStatus
): Promise<void> {
  const projectRef = doc(db, 'rb_projects', projectId);

  await updateDoc(projectRef, {
    status,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Count projects by status for a workspace
 * 
 * Phase D9: Used in Status Page
 */
export async function countProjectsByStatus(
  db: Firestore,
  workspaceId: string
): Promise<Record<ProjectStatus, number>> {
  const projects = await listProjects(db, workspaceId);

  const counts: Record<ProjectStatus, number> = {
    created: 0,
    extracting: 0,
    ready: 0,
    failed: 0,
  };

  for (const project of projects) {
    counts[project.status]++;
  }

  return counts;
}

/**
 * Get total project count for a workspace
 * 
 * Phase D9: Used in Status Page
 */
export async function getTotalProjectCount(
  db: Firestore,
  workspaceId: string
): Promise<number> {
  const projects = await listProjects(db, workspaceId);
  return projects.length;
}
